import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateValidator } from 'ng2-validation/dist/date';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { DateHelpers } from '@edl-development/edl-common';
import { CplCycle } from '../../../../model/cpl-cycle.model';
import { CplItem } from '../../../../model/cpl-item.model';
import { ComplianceItemService } from '../../../../services/compliance-item.service';
@Component({
    templateUrl: './modify-date-modal.component.html',
    styleUrls: ['./modify-date-modal.component.scss']
})

export class ModifyDateModalComponent implements OnInit {
    @Input() complianceItem: CplItem;
    @Input() cycles:  CplCycle[];
    @Input() individualAction:Boolean;
    @Input() cycle:CplCycle;

    modifyDueDateForm: UntypedFormGroup;
    busy: boolean;

    private affectedCycles: CplCycle[] = [];

    constructor(
        public activeModal: NgbActiveModal,
        private formBuilder: UntypedFormBuilder,
        private complianceItemService: ComplianceItemService,
        private toastr: ToastrService
    ) {}

    ngOnInit() {
        this.busy = false;
        let userCycles: CplCycle[];
        let MinDate: Date = DateHelpers.getMinDate();
        let EndDate: Date;
        let StartDate: Date;
        if (this.cycles == undefined) {
            userCycles = [];
            userCycles.push(this.cycle);
        }
        else {
            userCycles = this.cycles;
        }

        if (userCycles.length == 1) {
            EndDate = DateHelpers.shortDateStringToDate(userCycles[0].EndDate);
            EndDate = (EndDate > MinDate ? EndDate : null)
            StartDate = DateHelpers.shortDateStringToDate(userCycles[0].StartDate);
            StartDate = (StartDate > MinDate ? StartDate : null)
        }
        else {
            EndDate = null;
            StartDate = null;
        }

        this.affectedCycles = userCycles;

        this.modifyDueDateForm = this.formBuilder.group({
            ModifyDueDate: [(EndDate), [DateValidator]],
            ModifyStartDate: [(StartDate), [DateValidator]],
            ShouldSendNotifications: [true],
            Note: ['']
        }, {validator: [
            atLeastOne(Validators.required, ['ModifyDueDate', 'ModifyStartDate']),
            startBeforeOrEqualEnd('ModifyStartDate', 'ModifyDueDate', () => this.affectedCycles)
        ]});
    }

    submit({ value, valid }: { value: any, valid: boolean}) {

       this.busy = true;
       let userCycles: CplCycle[] = [];

            // Same toggle/note choice applied to every cycle in this action, one-off
            // or bulk.
            const shouldSendNotifications: boolean = value.ShouldSendNotifications;
            const note: string = shouldSendNotifications ? value.Note : null;

            // Start<=End is now enforced by startBeforeOrEqualEnd on the form itself
            // (submit is unreachable while invalid), so every cycle reaching this
            // point is guaranteed valid — no silent per-cycle drop, and the success
            // toast's count always matches what's actually being sent.
            if(this.individualAction != undefined && this.individualAction){

                // A blank field means "no change," not "clear" (per EDLP-3621
                // bounceback) — fall back to the cycle's current value on null,
                // same as the bulk branch below.
                this.cycle.StartDate = (value.ModifyStartDate == null ? this.cycle.StartDate : DateHelpers.DateToShortDate(value.ModifyStartDate));
                this.cycle.EndDate = (value.ModifyDueDate == null ? this.cycle.EndDate : DateHelpers.DateToShortDate(value.ModifyDueDate));
                this.cycle.ShouldSendNotifications = shouldSendNotifications;
                this.cycle.Note = note;
                userCycles.push(this.cycle);
            } else {
                // Bulk: these fields are always null-seeded regardless of any cycle's
                // actual date, so null means "no change" here too.
                this.cycles.forEach(cycle => {
                    cycle.StartDate = (value.ModifyStartDate == null ? cycle.StartDate : DateHelpers.DateToShortDate(value.ModifyStartDate));
                    cycle.EndDate = (value.ModifyDueDate == null ? cycle.EndDate : DateHelpers.DateToShortDate(value.ModifyDueDate));
                    cycle.ShouldSendNotifications = shouldSendNotifications;
                    cycle.Note = note;
                    userCycles.push(cycle);
                });
            }


        this.complianceItemService
            .updateUserAssignments(userCycles)
            .pipe(
                finalize(() => this.busy = false)
            )
            .subscribe((response: any) => {
                this.toastr.success(userCycles.length.toString() + ' user(s) due dates successfully modified.');
                this.activeModal.close();
            });
    }

    cancel() {
        this.activeModal.dismiss();
    }

    // edl-date-picker wraps ngx-bootstrap's bsDatepicker, which only commits the
    // input's text to its FormControl on native `change` (fires on blur) — so a
    // field cleared via keyboard still reads as its old value until the user
    // tabs/clicks away. That staleness both let Save & Close's disabled state
    // flip in the same tick as the click that was meant to trigger it (silently
    // swallowing the click) and let startBeforeOrEqualEnd compare a due date
    // against a start date the field no longer visibly holds. Flushing the
    // control to null the moment the field empties — via the native `input`
    // event, which bubbles up from edl-date-picker's inner <input> to its host
    // tag here — keeps form validity in sync with what's on screen instead of
    // waiting for blur.
    onDateFieldCleared(controlName: string, event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.value) {
            this.modifyDueDateForm.get(controlName)?.setValue(null);
        }
    }
}

// `controlNames` restricts which controls count toward "at least one" — without it,
// adding ShouldSendNotifications (a boolean, always non-null, so always
// Validators.required-valid) to the group would silently satisfy this check on its
// own, defeating the date-fields check entirely.
export const atLeastOne = (validator: ValidatorFn, controlNames: string[] = null) => (
    group: UntypedFormGroup,
  ): ValidationErrors | null => {
    const keys = controlNames ?? (group?.controls ? Object.keys(group.controls) : []);
    const hasAtLeastOne =
      group &&
      group.controls &&
      keys.some(k => !validator(group.controls[k]));

    return hasAtLeastOne ? null : { atLeastOne: true };
  };

// Previously, a Start Date left after the Due Date was silently dropped from the
// update in submit() rather than validated — the cycle just never made it into the
// request, producing a misleading "0 user(s) successfully modified" toast instead
// of a visible error, especially easy to hit editing only one of the two fields.
//
// When only one date is entered, a blank field always means "keep this cycle's
// existing date" (individual and bulk alike, per the EDLP-3621 bounceback), so
// the entered value must be checked against every affected cycle's own
// counterpart date, not just the (empty) other control. Cycles whose
// counterpart was never set (min-date sentinel) impose no constraint. All
// failure modes share the startAfterEnd key so the template message and
// disabled save need no awareness of which mode tripped.
export const startBeforeOrEqualEnd = (startControlName: string, endControlName: string, getAffectedCycles: () => CplCycle[] = () => []) => (
    group: UntypedFormGroup
  ): ValidationErrors | null => {
    const start = group?.get(startControlName)?.value;
    const end = group?.get(endControlName)?.value;

    if (start && end) {
        return (new Date(start) > new Date(end)) ? { startAfterEnd: true } : null;
    }

    const affectedCycles: CplCycle[] = getAffectedCycles() ?? [];

    if (start) {
        const enteredStart: Date = new Date(start);
        const startAfterAnExistingDue: boolean = affectedCycles.some(cycle => {
            const existingDue: Date | null = DateHelpers.shortDateStringToDate(cycle?.EndDate);
            return !DateHelpers.isMinDate(existingDue) && enteredStart > <Date>existingDue;
        });
        return startAfterAnExistingDue ? { startAfterEnd: true } : null;
    }

    if (end) {
        const enteredDue: Date = new Date(end);
        const dueBeforeAnExistingStart: boolean = affectedCycles.some(cycle => {
            const existingStart: Date | null = DateHelpers.shortDateStringToDate(cycle?.StartDate);
            return !DateHelpers.isMinDate(existingStart) && enteredDue < <Date>existingStart;
        });
        return dueBeforeAnExistingStart ? { startAfterEnd: true } : null;
    }

    return null;
  };